import { isEqual } from "es-toolkit";
import { atom } from "jotai";
import { atomFamily } from "jotai/utils";

import { Logger } from "@moment/logging";

import { LOCAL_WORKSPACE_NAME } from "../constants";
import { env } from "../env";
import { fs } from "../filesystem";
import { paths } from "../paths";

const logger = new Logger("related-file");

export const $isLoadingRelatedFile = atom(false);
export const $isErrorRelatedFile = atom(false);
export const $errorRelatedFile = atom(null as Error | null);

export const $readRelatedFile = atomFamily(
	({
		workspaceName,
		documentId,
		pageId,
		filename,
		src,
		token,
	}: {
		workspaceName: string;
		documentId: string;
		pageId: string;
		/** The filename in the related/ folder (for Moment-created images) */
		filename?: string;
		/** A path relative to the document root (for imported images with src attribute) */
		src?: string;
		token?: string;
	}) => {
		const $relatedFile = atom(async (get) => {
			// If src starts with "related/", treat it as a filename in the related folder
			// This handles legacy documents where toDOM used to output src="related/${file}"
			const normalizedSrc = src && src.startsWith("related/") ? undefined : src;
			const normalizedFilename =
				src && src.startsWith("related/") ? src.slice("related/".length) : filename;

			if (workspaceName === LOCAL_WORKSPACE_NAME) {
				const fsApi = fs();
				const pathsApi = paths();

				let path: string;
				if (normalizedSrc) {
					// Load from document-relative path (for imported images)
					path = await get(
						pathsApi.getDocumentRelativeFilePath({
							documentId,
							relativePath: normalizedSrc,
						})
					);
				} else if (normalizedFilename) {
					// Load from related/ folder (for Moment-created images)
					path = await get(
						pathsApi.getPageRelatedFilePath({
							documentId,
							pageId,
							filename: normalizedFilename,
						})
					);
				} else {
					throw new Error("Either filename or src must be provided");
				}

				const contents = await get(fsApi.readBinaryFile({ filePath: path }));
				return contents;
			}

			// get it from gitea
			const headers = new Headers();
			if (token) {
				headers.set("Authorization", `Bearer ${token}`);
			}

			// Use src directly if provided, otherwise use the related/ folder path
			const filePath =
				normalizedSrc ??
				`pages/${pageId}/related/${encodeURIComponent(normalizedFilename ?? "")}`;
			const response = await fetch(
				`${env.docsApiUrl}/api/v2/${workspaceName}/${documentId}/raw/${filePath}?ref=main`,
				{ headers }
			);
			if (!response.ok) {
				logger.error("Failed to fetch related file", {
					isAuthenticated: !!token,
					cause: response.statusText,
				});
				throw new Error("Failed to fetch related file", { cause: response.statusText });
			}

			const documentContents = await response.arrayBuffer();

			return documentContents;
		});
		const label = src ?? filename ?? "unknown";
		$relatedFile.debugLabel = `readRelatedFile/${documentId}/${pageId}/${label}`;
		return $relatedFile;
	},
	isEqual
);
